import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import type { BookingStatus } from '@/types/events';

export interface UserBooking {
  id: string;
  event_id: string;
  producer_id: string;
  occurrence_id: string | null;
  spots: number;
  status: BookingStatus;
  confirmation_token: string;
  booking_code: string;
  created_at: string;
  cancelled_at: string | null;
  event_title: string;
  event_start_date: string;
  event_end_date: string;
  producer_name: string;
}

interface UseMyBookingsResult {
  bookings: UserBooking[];
  isLoading: boolean;
  error: Error | null;
  cancelBooking: (token: string) => Promise<void>;
  isCancelling: boolean;
}

interface UseMyBookingsOptions {
  enabled?: boolean;
}

export function useMyBookings(options: UseMyBookingsOptions = {}): UseMyBookingsResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryEnabled = options.enabled ?? true;

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Fetch bookings where user_id matches OR guest_email matches user's email
      // We query by user_id first (for authenticated bookings)
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('event_bookings')
        .select(`
          id,
          event_id,
          producer_id,
          occurrence_id,
          spots,
          status,
          confirmation_token,
          booking_code,
          created_at,
          cancelled_at,
          guest_email
        `)
        .or(`user_id.eq.${user.id},guest_email_norm.eq.${user.email?.toLowerCase()}`)
        .order('created_at', { ascending: false });

      if (bookingsError) throw bookingsError;

      if (!bookingsData || bookingsData.length === 0) {
        return [];
      }

      // Get unique event IDs and producer IDs
      const eventIds = [...new Set(bookingsData.map((b) => b.event_id))];
      const producerIds = [...new Set(bookingsData.map((b) => b.producer_id))];

      // Fetch events
      const { data: eventsData, error: eventsError } = await supabase
        .from('producer_events')
        .select('id, title, start_date, end_date')
        .in('id', eventIds);

      if (eventsError) throw eventsError;

      // Fetch producers
      const { data: producersData, error: producersError } = await supabase
        .from('producers')
        .select('id, name')
        .in('id', producerIds);

      if (producersError) throw producersError;

      // Create lookup maps
      const eventMap = new Map(eventsData?.map((e) => [e.id, e]) ?? []);
      const producerMap = new Map(producersData?.map((p) => [p.id, p]) ?? []);

      // Combine data
      const bookings: UserBooking[] = bookingsData.map((booking) => {
        const event = eventMap.get(booking.event_id);
        const producer = producerMap.get(booking.producer_id);

        return {
          id: booking.id,
          event_id: booking.event_id,
          producer_id: booking.producer_id,
          occurrence_id: booking.occurrence_id ?? null,
          spots: booking.spots,
          status: booking.status as BookingStatus,
          confirmation_token: booking.confirmation_token,
          booking_code: booking.booking_code,
          created_at: booking.created_at,
          cancelled_at: booking.cancelled_at,
          event_title: event?.title ?? 'Okänt event',
          event_start_date: event?.start_date ?? '',
          event_end_date: event?.end_date ?? '',
          producer_name: producer?.name ?? 'Okänd arrangör',
        };
      });

      return bookings;
    },
    enabled: queryEnabled && !!user,
  });

  const cancelMutation = useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.functions.invoke('cancel-event-booking', {
        body: { token },
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Kunde inte avboka');

      return data;
    },
    onSuccess: () => {
      // Invalidate to refetch bookings
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
    },
  });

  return {
    bookings: data ?? [],
    isLoading,
    error: error as Error | null,
    cancelBooking: cancelMutation.mutateAsync,
    isCancelling: cancelMutation.isPending,
  };
}
